import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../auth";
import { Icon } from "../Icon";
import Markdown from "../Markdown";
import { useSeo } from "../ui";
import { fmtDate } from "./Forum";

/* 바이트 → 사람이 읽는 단위 */
function formatSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

async function call(path, options = {}) {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: options.body ? { "Content-Type": "application/json" } : undefined,
    ...options,
  });
  if (res.status === 204) return null;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(typeof data.detail === "string" ? data.detail : "요청을 처리하지 못했습니다.");
  }
  return data;
}

function Attachments({ items, loggedIn, from }) {
  if (!items.length) return null;
  return (
    <section className="forum-files" aria-label="첨부 자료">
      <h2 className="forum-files__title">첨부 자료</h2>
      <ul>
        {items.map((f) => {
          const locked = f.members_only && !loggedIn;
          return (
            <li key={f.id} className="forum-file">
              <div className="forum-file__main">
                <strong>
                  {f.title}
                  {f.version && <span className="badge badge--accent">v{f.version}</span>}
                </strong>
                <span className="forum-file__meta">
                  <span className="forum-file__name" title={f.filename}>{f.filename}</span>
                  <span>{formatSize(f.size)}</span>
                  {f.downloads > 0 && <span>{f.downloads.toLocaleString("ko-KR")}회 다운로드</span>}
                  {f.members_only && (
                    <span className="res__lock">
                      <Icon.Shield width="13" height="13" aria-hidden="true" focusable="false" />
                      회원 전용
                    </span>
                  )}
                </span>
              </div>
              {locked ? (
                <Link className="btn btn--ghost forum-file__btn" to="/login" state={{ from }}>
                  <Icon.Shield width="16" height="16" aria-hidden="true" focusable="false" />
                  로그인 후 다운로드
                </Link>
              ) : (
                <a className="btn btn--ghost forum-file__btn" href={f.url} download>
                  <Icon.Download width="16" height="16" aria-hidden="true" focusable="false" />
                  다운로드
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function CommentForm({ postId, parentId, secretFixed, onDone, onCancel, compact }) {
  const [body, setBody] = useState("");
  const [secret, setSecret] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    if (!body.trim()) { setError("내용을 입력해 주세요."); return; }
    setBusy(true); setError("");
    try {
      await call(`/api/forum/${postId}/comments`, {
        method: "POST",
        body: JSON.stringify({ body: body.trim(), is_secret: secret, parent_id: parentId || null }),
      });
      setBody(""); setSecret(false);
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className={`comment-form ${compact ? "comment-form--reply" : ""}`} onSubmit={submit}>
      <textarea
        className="form__input form__textarea"
        rows={compact ? 3 : 4}
        maxLength={2000}
        placeholder={compact ? "답글을 입력하세요" : "궁금한 점이나 의견을 남겨 주세요. 운영자가 확인 후 답변드립니다."}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        disabled={busy}
      />
      <div className="comment-form__foot">
        {parentId ? (
          secretFixed && <span className="comment-form__hint">비밀 댓글의 답글은 비밀로 남습니다.</span>
        ) : (
          <label className="comment-form__secret">
            <input type="checkbox" checked={secret} onChange={(e) => setSecret(e.target.checked)} disabled={busy} />
            비밀 댓글 <span>— 나와 운영자만 볼 수 있습니다</span>
          </label>
        )}
        <span className="comment-form__count">{body.length}/2000</span>
        {onCancel && (
          <button type="button" className="btn btn--ghost comment-form__btn" onClick={onCancel} disabled={busy}>취소</button>
        )}
        <button type="submit" className="btn btn--primary comment-form__btn" disabled={busy}>
          {busy ? "등록 중…" : compact ? "답글 등록" : "댓글 등록"}
        </button>
      </div>
      {error && <p className="comment-form__error" role="alert">{error}</p>}
    </form>
  );
}

function CommentBody({ c }) {
  if (c.deleted) return <p className="comment__body comment__body--muted">삭제된 댓글입니다.</p>;
  if (c.hidden) {
    return (
      <p className="comment__body comment__body--muted">
        <Icon.Shield width="14" height="14" aria-hidden="true" focusable="false" /> 비밀 댓글입니다.
      </p>
    );
  }
  return <p className="comment__body">{c.body}</p>;
}

function CommentHead({ c, onDelete }) {
  return (
    <div className="comment__head">
      <span className="comment__author">{c.author}</span>
      {c.is_admin && <span className="forum-tag forum-tag--admin">운영자</span>}
      {c.mine && <span className="comment__mine">내 댓글</span>}
      {c.is_secret && !c.deleted && !c.hidden && (
        <span className="comment__secret">
          <Icon.Shield width="12" height="12" aria-hidden="true" focusable="false" /> 비밀
        </span>
      )}
      <span className="comment__date">{fmtDate(c.created_at, true)}</span>
      {c.mine && !c.deleted && (
        <button type="button" className="comment__del" onClick={() => onDelete(c)}>삭제</button>
      )}
    </div>
  );
}

function Comments({ postId, from }) {
  const { member, ready } = useAuth();
  const { hash } = useLocation();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [replyTo, setReplyTo] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await call(`/api/forum/${postId}/comments`));
      setError("");
    } catch (err) {
      setError(err.message);
    }
  }, [postId]);

  useEffect(() => { if (ready) load(); }, [load, ready, member]);

  /* 메일의 '#comments' 링크로 들어오면 댓글을 다 불러온 뒤 그 자리로 */
  useEffect(() => {
    if (data && hash === "#comments") {
      document.getElementById("comments")?.scrollIntoView({ block: "start" });
    }
  }, [data, hash]);

  async function remove(c) {
    if (!window.confirm("이 댓글을 삭제할까요?")) return;
    try {
      await call(`/api/forum/comments/${c.id}`, { method: "DELETE" });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <section id="comments" className="comments">
      <h2 className="comments__title">
        댓글 · 문의 <span>{data ? data.total : ""}</span>
      </h2>

      {ready && member ? (
        <CommentForm postId={postId} onDone={load} />
      ) : (
        <div className="comments__login">
          <p>댓글은 로그인 후 남기실 수 있습니다. 가입은 무료입니다.</p>
          <Link className="btn btn--ghost" to="/login" state={{ from: `${from}#comments` }}>로그인 · 회원가입</Link>
        </div>
      )}

      {error && <p className="comment-form__error" role="alert">{error}</p>}

      {data && data.items.length === 0 && (
        <p className="comments__empty">아직 댓글이 없습니다. 첫 질문을 남겨 주세요.</p>
      )}

      {data && data.items.length > 0 && (
        <ul className="comment-list">
          {data.items.map((c) => {
            const canReply = member && !c.deleted && !c.hidden;
            return (
              <li key={c.id} className="comment">
                <CommentHead c={c} onDelete={remove} />
                <CommentBody c={c} />
                {canReply && replyTo !== c.id && (
                  <button type="button" className="comment__reply-btn" onClick={() => setReplyTo(c.id)}>답글</button>
                )}

                {(c.replies.length > 0 || replyTo === c.id) && (
                  <ul className="comment-replies">
                    {c.replies.map((r) => (
                      <li key={r.id} className={`comment comment--reply ${r.is_admin ? "comment--admin" : ""}`}>
                        <CommentHead c={r} onDelete={remove} />
                        <CommentBody c={r} />
                      </li>
                    ))}
                    {replyTo === c.id && (
                      <li className="comment comment--reply">
                        <CommentForm
                          postId={postId}
                          parentId={c.id}
                          secretFixed={c.is_secret}
                          compact
                          onCancel={() => setReplyTo(null)}
                          onDone={() => { setReplyTo(null); load(); }}
                        />
                      </li>
                    )}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default function ForumPost() {
  const { id } = useParams();
  const { member } = useAuth();
  const [state, setState] = useState({ status: "loading", post: null });

  useEffect(() => {
    let alive = true;
    setState({ status: "loading", post: null });
    (async () => {
      try {
        const res = await fetch(`/api/forum/${encodeURIComponent(id)}`);
        if (res.status === 404 || res.status === 422) {
          if (alive) setState({ status: "missing", post: null });
          return;
        }
        if (!res.ok) throw new Error();
        const post = await res.json();
        if (alive) setState({ status: "ready", post });
      } catch {
        if (alive) setState({ status: "error", post: null });
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const post = state.post;
  useSeo({
    title: post ? `${post.title} — 포럼 | I.META` : "포럼 | I.META",
    description: post?.summary || undefined,
    path: `/forum/${id}`,
  });

  const from = `/forum/${id}`;

  return (
    <article className="forum-post">
      <div className="forum-post__inner">
        <Link to={post ? `/forum?cat=${post.category}` : "/forum"} className="forum-post__back">
          ← 포럼 {post ? `· ${post.category_label}` : ""}
        </Link>

        {state.status === "loading" && <p className="res-empty">글을 불러오는 중입니다…</p>}
        {state.status === "error" && <p className="res-empty">글을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.</p>}
        {state.status === "missing" && (
          <div className="res-empty res-empty--box">
            <h1 className="res-empty__title">글을 찾을 수 없습니다</h1>
            <p>삭제되었거나 비공개로 바뀐 글입니다. <Link to="/forum">포럼 목록</Link>으로 돌아가 주세요.</p>
          </div>
        )}

        {post && (
          <>
            <header className="forum-post__head">
              <div className="forum-item__top">
                {post.pinned && <span className="forum-tag forum-tag--pin">공지</span>}
                <span className="forum-tag">{post.category_label}</span>
              </div>
              <h1 className="forum-post__title">{post.title}</h1>
              <div className="forum-item__meta">
                <span>{fmtDate(post.created_at)}</span>
                <span>조회 {post.views.toLocaleString("ko-KR")}</span>
                <a href="#comments">댓글 {post.comments}</a>
              </div>
            </header>

            <Markdown source={post.body} className="forum-post__body" />

            <Attachments items={post.attachments} loggedIn={!!member} from={from} />

            <p className="note-line">
              공개한 자료는 무료이며, 사용에 따른 결과는 보증하지 않습니다. 상업적 이용이나 재배포 전에는
              한 번 문의해 주시면 감사하겠습니다.
            </p>

            <Comments postId={post.id} from={from} />

            {(post.prev || post.next) && (
              <nav className="forum-post__nav" aria-label="이전 · 다음 글">
                {post.next ? (
                  <Link to={`/forum/${post.next.id}`}><span>다음 글</span>{post.next.title}</Link>
                ) : <span />}
                {post.prev ? (
                  <Link to={`/forum/${post.prev.id}`}><span>이전 글</span>{post.prev.title}</Link>
                ) : <span />}
              </nav>
            )}
          </>
        )}
      </div>
    </article>
  );
}
